/**
 * AnswerDisplay — Compact read-only display of a saved checklist answer
 *
 * Shows the answer value formatted by type, source badge (Manual vs AI),
 * timestamp, and an optional edit button (wired in phase 3).
 */

import { useState } from "react";
import { Pencil, ChevronDown, ChevronUp, Bot, User } from "lucide-react";
import type { ChecklistItemAnswer } from "@/packages/types";

// ─── Props ──────────────────────────────────────────────────────────────────

interface AnswerDisplayProps {
  answer: ChecklistItemAnswer;
  onEdit?: () => void;
}

// ─── Answer Value Formatters ────────────────────────────────────────────────

function formatAnswerValue(answer: ChecklistItemAnswer): {
  text: string;
  dotColor?: string;
} {
  const questionType = answer.question.type;

  if (questionType === "yes_no") {
    const isYes = answer.answer === true || answer.answer === "true";
    return {
      text: isYes ? "Yes (Compliant)" : "No (Non-compliant)",
      dotColor: isYes ? "bg-green-500" : "bg-red-500",
    };
  }

  if (questionType === "date") {
    const ts =
      typeof answer.answer === "number" ? answer.answer : Number(answer.answer);
    if (!Number.isNaN(ts)) {
      const formatted = new Date(ts).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return { text: formatted };
    }
    return { text: String(answer.answer) };
  }

  // text, document, selection
  return { text: String(answer.answer) };
}

// ─── Source Badge ────────────────────────────────────────────────────────────

function SourceBadge({ answer }: { answer: ChecklistItemAnswer }) {
  if (answer.source === "ai_extracted") {
    const score =
      answer.confidenceScore != null
        ? Math.round(answer.confidenceScore * 100)
        : null;
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">
        <Bot size={10} className="shrink-0" />
        AI{score != null ? ` \u00B7 ${score}%` : ""}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/10 px-2 py-0.5 text-[10px] font-medium text-muted">
      <User size={10} className="shrink-0" />
      Manual
    </span>
  );
}

// ─── Source Documents Excerpt ────────────────────────────────────────────────

function SourceDocumentsSection({ answer }: { answer: ChecklistItemAnswer }) {
  const [isOpen, setIsOpen] = useState(false);
  const docs = answer.sourceDocuments;

  if (!docs || docs.length === 0 || answer.source !== "ai_extracted") {
    return null;
  }

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-secondary transition-colors"
      >
        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {docs.length} source {docs.length === 1 ? "document" : "documents"}
      </button>
      {isOpen && (
        <div className="mt-1.5 space-y-1.5">
          {docs.map((doc, i) => (
            <div
              key={`${doc.documentId}-${i}`}
              className="rounded border border-border/60 bg-card/50 px-2.5 py-1.5"
            >
              <p className="text-[11px] text-secondary italic leading-relaxed">
                &ldquo;{doc.excerpt}&rdquo;
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function AnswerDisplay({ answer, onEdit }: AnswerDisplayProps) {
  const { text, dotColor } = formatAnswerValue(answer);
  const timestamp = new Date(answer.answeredAt).toLocaleDateString();

  return (
    <div className="rounded-lg border border-border/60 bg-card/50 px-3 py-2.5 space-y-2">
      {/* Answer value */}
      <div className="flex items-center gap-2">
        {dotColor && (
          <span className={`size-2 rounded-full shrink-0 ${dotColor}`} />
        )}
        <span className="text-[13px] font-medium text-primary">{text}</span>
      </div>

      {/* Meta row: source + timestamp + edit */}
      <div className="flex items-center gap-2 flex-wrap">
        <SourceBadge answer={answer} />
        <span className="text-[10px] text-muted">Answered {timestamp}</span>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
          >
            <Pencil size={10} />
            Edit
          </button>
        )}
      </div>

      {/* Collapsible source documents */}
      <SourceDocumentsSection answer={answer} />
    </div>
  );
}
